import { Controller } from "@hotwired/stimulus"

const BREAKPOINTS = [
  { minWidth: 1200, columns: 3 },
  { minWidth: 768, columns: 2 },
  { minWidth: 0, columns: 1 },
]

// CSS column masonry keeps unused columns on the right — shrink and center when fewer columns are filled.
export default class extends Controller {
  static values = { count: Number }

  connect() {
    this.boundLayout = () => this.scheduleLayout()
    this.resizeObserver = new ResizeObserver(this.boundLayout)
    this.resizeObserver.observe(this.element)
    window.addEventListener("resize", this.boundLayout)
    this.scheduleLayout()
  }

  disconnect() {
    this.resizeObserver?.disconnect()
    cancelAnimationFrame(this.layoutFrame)
    window.removeEventListener("resize", this.boundLayout)
  }

  countValueChanged() {
    this.scheduleLayout()
  }

  scheduleLayout() {
    cancelAnimationFrame(this.layoutFrame)
    this.layoutFrame = requestAnimationFrame(() => this.applyWidth())
  }

  maxColumns() {
    // Keep in sync with `.index-search-results--cards` column breakpoints (viewport media queries).
    const width = window.innerWidth
    return BREAKPOINTS.find((breakpoint) => width >= breakpoint.minWidth)?.columns ?? 1
  }

  usedColumns(count, maxCols) {
    if (count <= 0) return maxCols
    // CSS columns fill left-to-right (1..n in col 1, then col 2…). Any count > maxCols uses every column.
    return Math.min(count, maxCols)
  }

  applyWidth() {
    const count = this.countValue || 0
    const maxCols = this.maxColumns()
    const usedCols = this.usedColumns(count, maxCols)

    if (usedCols >= maxCols) {
      this.element.style.removeProperty("max-width")
      this.element.style.removeProperty("columns")
      return
    }

    const gap =
      getComputedStyle(this.element).getPropertyValue("--turbo-index-gutter").trim() || "0.9rem"
    this.element.style.maxWidth = `calc((100% - (${maxCols} - 1) * ${gap}) / ${maxCols} * ${usedCols} + (${usedCols} - 1) * ${gap})`
    this.element.style.columns = String(usedCols)
  }
}
